import { useCallback, useMemo } from 'react';
import { backendApi } from '@common/reducers/backend-api/backend-api';

/**
 * Resolve a WhatsApp chatId → its human-readable name (e.g. "My Love ❤️") for
 * the account, so the my-bridge UI shows chat NAMES instead of raw ids/numbers.
 *
 * Single source of truth for both the agent dialog's chat pickers and the
 * time-trigger rows. The `my-bridge/chats` endpoint returns chats keyed by `id`
 * (matching the contact picker's SelectedContact.id) — the type also declares
 * `chatId`, so we accept either. `nameOf` falls back to the bare id (suffix
 * stripped) until the list loads or when a chat isn't found.
 *
 * rev 3.3 §6.17.1: no connection/project parameter — the account is the tenant
 * (the query itself sends the legacy 'default' clientId older backends require).
 */
const stripChatSuffix = (id: string): string =>
  id.replace(/@(c\.us|g\.us|newsletter|lid)$/, '');

export function useBridgeChatNames(): { nameOf: (id: string) => string } {
  const { data: chats } = backendApi.useGetMyBridgeChatsQuery();
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    (chats ?? []).forEach((c) => {
      const cid = c.id ?? c.chatId;
      if (cid && c.name) m.set(cid, c.name);
    });
    return m;
  }, [chats]);
  const nameOf = useCallback((id: string): string => nameById.get(id) || stripChatSuffix(id), [nameById]);
  return { nameOf };
}
